import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "../store";
import axios from 'axios';


// Redux

// ----------------------------------------------------------------------

let initialState = {
    clientList: { 'rows': [] },
    clientActivityOptions: [],
    isClientActivityLoading: false,
    // Optional: follow-up related state (could be expanded later)
    isClientFollowUpSaving: false,
    clientProfileData: null,
    clientProfileLoading: false,
    clientLogbookData: [],
    clientLogbookLoading: true,
    // Search + attach relationships
    attachProfessionalOptions: [],
    attachCompanyOptions: [],
    isAttachSearchLoading: false,
    isClientRelationSubmitting: false,
    searchTerm: "",
    inputValue: "",
    sortColumn: "",
    sortDirection: "asc",
    currentPage: 1,
    typeFilter: "All",
    tagsFilter: "All",
    professionFilter: "All",
    preferredFilter: "All",
    clientListLoading: true,
    isDeletingClient: false,
    error: null,
};

export const slice = createSlice({
    name: "client",
    initialState,
    reducers: {
        // set filter values in redux
        setClientList(state, action) {
            state.clientList = action.payload;
        },

        setTypeFilter(state, action) {
            state.typeFilter = action.payload;
        },

        setTagsFilter(state, action) {
            state.tagsFilter = action.payload;
        },

        setProfessionFilter(state, action) {
            state.professionFilter = action.payload;
        },

        setPreferredFilter(state, action) {
            state.preferredFilter = action.payload;
        },

        setSearchTerm(state, action) {
            state.searchTerm = action.payload;
        },

        setClientListLoading(state, action) {
            state.clientListLoading = action.payload;
        },

        setClientActivityOptions(state, action) {
            state.clientActivityOptions = action.payload;
            state.isClientActivityLoading = false;
        },

        setClientActivityLoading(state, action) {
            state.isClientActivityLoading = action.payload;
        },

        setClientFollowUpSaving(state, action) {
            state.isClientFollowUpSaving = action.payload;
        },

        setClientProfile(state, action) {
            state.clientProfileData = action.payload;
            state.clientProfileLoading = false;
        },

        setClientProfileLoading(state, action) {
            state.clientProfileLoading = action.payload;
        },

        setClientLogbook(state, action) {
            state.clientLogbookData = action.payload;
            state.clientLogbookLoading = false;
        },

        setClientLogbookLoading(state, action) {
            state.clientLogbookLoading = action.payload;
        },

        setAttachProfessionalOptions(state, action) {
            state.attachProfessionalOptions = action.payload || [];
        },

        setAttachCompanyOptions(state, action) {
            state.attachCompanyOptions = action.payload || [];
        },

        setAttachSearchLoading(state, action) {
            state.isAttachSearchLoading = action.payload;
        },

        setClientRelationSubmitting(state, action) {
            state.isClientRelationSubmitting = action.payload;
        },

        setinputValue(state, action) {
            state.inputValue = action.payload;
        },

        setSortDirection(state, action) {
            state.sortDirection = action.payload;
        },

        setSortColumn(state, action) {
            state.sortColumn = action.payload;
        },

        setCurrentPage(state, action) {
            state.currentPage = action.payload;
        },

        setDeletingClient(state, action) {
            state.isDeletingClient = action.payload;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isinvSummaryTableLoading = false;
            state.error = action.payload;
        },

    },
});

export const {
    setTypeFilter,
    setTagsFilter,
    setProfessionFilter,
    setPreferredFilter,
    setSearchTerm,
    setClientListLoading,
    setClientActivityOptions,
    setClientActivityLoading,
    setClientFollowUpSaving,
    setClientProfile,
    setClientProfileLoading,
    setClientLogbook,
    setClientLogbookLoading,
    setAttachProfessionalOptions,
    setAttachCompanyOptions,
    setAttachSearchLoading,
    setClientRelationSubmitting,
    setinputValue,
    setCurrentPage,
    setSortColumn,
    setSortDirection,
    setPaginationLoading,
    setDeletingClient,
    hasError,
} = slice.actions

export default slice.reducer;

// Fetch a single client profile
export function getClientProfile(clientID) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            dispatch(slice.actions.setClientProfileLoading(true));
            const res = await axios.get(`${API_URL}/api/clients/clientProfile/clientInfo?clientID=${clientID}`);
            dispatch(slice.actions.setClientProfile(res.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Fetch a client's logbook
export function getClientLogbook(clientID) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            dispatch(slice.actions.setClientLogbookLoading(true));
            const res = await axios.get(`${API_URL}/api/clients/clientProfile/clientLogBook?clientID=${clientID}`);
            dispatch(slice.actions.setClientLogbook(res.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getClientsList(typeFilter, tagsFilter, professionFilter, preferredFilter, searchTerm, page, rowsPerPage, sortColumn, sortDirection) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        dispatch(slice.actions.setClientListLoading(true));
        try {
            // Construct the query string
            const queryParams = new URLSearchParams({
                typeFilter,
                tagsFilter,
                professionFilter,
                preferredFilter,
                page,
                rowsPerPage,
                searchTerm,
                sortColumn,
                sortDirection
            }).toString();


            const response = await axios.get(`${API_URL}/api/clients/all?${queryParams}`);
            dispatch(slice.actions.setClientList(response.data.data));
            dispatch(slice.actions.setClientListLoading(false));

        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setClientListLoading(false));

        }
    };
}

export function getClientActivityOptions() {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        dispatch(slice.actions.setClientActivityLoading(true));
        try {
            // Helper to normalize various response shapes into a flat string array
            const normalizeOptions = (input) => {
                if (!input) return [];
                if (Array.isArray(input)) {
                    if (input.every((x) => typeof x === 'string')) return input;
                    // Handle arrays of objects like { activity, id }
                    const mapped = input.map((x) => {
                        if (x && typeof x === 'object') {
                            return x.activity || x.name || x.label || x.value;
                        }
                        return undefined;
                    }).filter((v) => typeof v === 'string' && v.trim().length > 0);
                    return Array.from(new Set(mapped));
                }
                const dataNode = Array.isArray(input?.data) ? input.data : input?.data;
                if (Array.isArray(dataNode)) {
                    if (dataNode.every((x) => typeof x === 'string')) return dataNode;
                    const mapped = dataNode.map((x) => {
                        if (x && typeof x === 'object') {
                            return x.activity || x.name || x.label || x.value;
                        }
                        return undefined;
                    }).filter((v) => typeof v === 'string' && v.trim().length > 0);
                    return Array.from(new Set(mapped));
                }

                const possibleKeys = ['activities', 'activityOptions', 'options', 'list', 'values'];
                for (const key of possibleKeys) {
                    if (Array.isArray(input?.[key])) return input[key].filter((x) => typeof x === 'string');
                    if (Array.isArray(dataNode?.[key])) return dataNode[key].filter((x) => typeof x === 'string');
                }

                // If object is a map of arrays, flatten unique strings
                const source = typeof input === 'object' ? input : (typeof dataNode === 'object' ? dataNode : null);
                if (source && !Array.isArray(source)) {
                    const collected = [];
                    Object.values(source).forEach((val) => {
                        if (Array.isArray(val)) {
                            val.forEach((s) => { if (typeof s === 'string') collected.push(s); });
                        }
                    });
                    return Array.from(new Set(collected));
                }
                return [];
            };

            // Use the new canonical endpoint
            const res = await axios.get(`${API_URL}/api/clients/activity/list`);
            console.log(res.data);
            let opts = normalizeOptions(res.data.data);
            if (!Array.isArray(opts) || opts.length === 0) {
                opts = ['Update', 'Feedback', 'Google Review', 'Bad Debtor'];
            }
            dispatch(slice.actions.setClientActivityOptions(opts));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setClientActivityOptions(['Update', 'Feedback', 'Google Review', 'Bad Debtor']));
        }
    };
}

// Generic client search used for attaching Professionals/Companies to a client.
// NOTE: This reuses the existing bid/opportunity client search endpoint so the
// backend contract stays consistent with other areas (e.g. Opportunities forms).
export function searchClientsForAttachment(searchTerm, clientType) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        if (!searchTerm || !clientType) return;
        try {
            dispatch(slice.actions.setAttachSearchLoading(true));
            const response = await axios.get(`${API_URL}/api/bid/opportunity/client`, {
                params: {
                    clientName: searchTerm,
                    clientType,
                },
            });

            const formatted =
                Array.isArray(response.data?.data)
                    ? response.data.data.map((client) => ({
                        value: client.id,
                        label: client.name,
                    }))
                    : [];

            if (clientType === 'professional') {
                dispatch(slice.actions.setAttachProfessionalOptions(formatted));
            } else if (clientType === 'company') {
                dispatch(slice.actions.setAttachCompanyOptions(formatted));
            }
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        } finally {
            dispatch(slice.actions.setAttachSearchLoading(false));
        }
    };
}

// Set a follow-up date for a client marked as Person of Interest
export function setClientFollowUpDate(clientID, followUpDate, updatedBy) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            dispatch(slice.actions.setClientFollowUpSaving(true));
            const payload = {
                clientID: Number(clientID),
                followUpDate,
                updatedBy,
            };
            await axios.post(`${API_URL}/api/clients/follow/up/date`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        } finally {
            dispatch(slice.actions.setClientFollowUpSaving(false));
        }
    };
}

// Attach a related client (e.g. Professional or Company) to a base client.
// relationType should be a semantic string understood by the backend, e.g.
// 'professional' or 'company'.
export function attachClientRelation({ clientID, relatedClientID, relationType, updatedBy }) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            dispatch(slice.actions.setClientRelationSubmitting(true));
            const body = {
                clientID: Number(clientID),
                relatedClientID: Number(relatedClientID),
                relationType,
                updatedBy,
            };
            await axios.post(`${API_URL}/api/clients/attach/relation`, body, {
                headers: { 'Content-Type': 'application/json' },
            });

            // Refresh profile so new relationships are visible immediately
            dispatch(getClientProfile(clientID));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            // Bubble up so callers can surface user feedback if needed
            throw error;
        } finally {
            dispatch(slice.actions.setClientRelationSubmitting(false));
        }
    };
}

// Detach client relation
export function detachClientRelation({ clientID, relatedClientID, relationType, updatedBy }) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            dispatch(slice.actions.setClientRelationSubmitting(true));
            const body = {
                clientID: Number(clientID),
                relatedClientID: Number(relatedClientID),
                relationType,
                updatedBy,
            };
            await axios.delete(`${API_URL}/api/clients/detach/relation`, {
                data: body,
                headers: { 'Content-Type': 'application/json' },
            });

            // Refresh profile so removed relationships are no longer visible
            dispatch(getClientProfile(clientID));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            // Bubble up so callers can surface user feedback if needed
            throw error;
        } finally {
            dispatch(slice.actions.setClientRelationSubmitting(false));
        }
    };
}

// Post a client logbook entry
export function postClientLogbook(clientID, activity, comment, email) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            const body = {
                clientID,
                activity,
                comment,
                email,
            };
            await axios.post(`${API_URL}/api/clients/submitClientLogbook`, body, {
                headers: { 'Content-Type': 'application/json' }
            });
            // After successful post, refresh profile and logbook
            dispatch(getClientProfile(clientID));
            dispatch(getClientLogbook(clientID));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Update client profile information
export function updateClientProfile(UpdatedValues, email, RowID) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async () => {
        try {
            const body = {
                Action: 'UPDATE',
                SectionName: 'ClientProfile',
                UpdatedValues,
                email,
                RowID,
            };
            const response = await axios.post(`${API_URL}/api/clients/UpdatingInformation`, body, {
                headers: { 'Content-Type': 'application/json' }
            });
            // Refresh profile after update
            dispatch(getClientProfile(RowID));
            return response.data;
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function fetchClientExport() {
    const API_URL = process.env.REACT_APP_API_URL;
    return async (dispatch) => {
        try {
            // Make an API call to request the client data in CSV format
            const response = await axios.get(`${API_URL}/api/export/clients`, {
                responseType: 'blob',  // Setting responseType to 'blob' to handle binary data.
            });

            // Create a Blob object from the response data
            const file = new Blob([response.data], { type: 'text/csv' });

            // Generate a URL for the Blob object
            const fileURL = URL.createObjectURL(file);

            // Create a temporary anchor element to enable file download
            const tempLink = document.createElement('a');
            tempLink.href = fileURL;
            tempLink.setAttribute('download', 'Clients Dashboard.csv'); // Setting the default file name for CSV
            tempLink.style.display = 'none';
            document.body.appendChild(tempLink);
            tempLink.click(); // Trigger the download
            document.body.removeChild(tempLink);

            // Revoke the Blob URL to free up resources
            URL.revokeObjectURL(fileURL);

            // Dispatch a success action, could update state to show download was successful
            dispatch({ type: 'DOWNLOAD_SUCCESS' });
        } catch (error) {
            // Log error if the export process fails and dispatch an error state update
            console.error('Error during export:', error);
            dispatch({ type: 'DOWNLOAD_ERROR', error });
        }
    };
}

// Delete a client
export function deleteClient(clientID, email) {
    const API_URL = process.env.REACT_APP_API_URL;
    return async (dispatch) => {
        try {
            dispatch(slice.actions.setDeletingClient(true));
            const body = {
                Action: 'DELETE',
                SectionName: 'ClientProfile',
                email,
                RowID: clientID,
            };
            await axios.delete(`${API_URL}/api/clients/delete`, {
                data: body,
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            throw error;
        } finally {
            dispatch(slice.actions.setDeletingClient(false));
        }
    };
}

// export function getInvSummaryCards() {
//     const API_URL = process.env.REACT_APP_API_URL;
//     return async () => {
//         dispatch(slice.actions.setInvSummaryCardLoading(true));
//         try {
//             const response = await axios.get(`${API_URL}/api/invoicing/summary/cards`);
//             dispatch(slice.actions.setInvoiceSummaryCards(response.data.data));
//         } catch (error) {
//             console.error(error);
//             dispatch(slice.actions.hasError(error));
//         }
//     };
// }
